import { observable, configure, action } from 'mobx';
import LocalModel from './localModel';
import util from 'preact-util';

configure({ enforceActions: 'always' });

const hostname = () => (typeof window !== 'undefined' ? window.location.hostname : '');

class ElearningProgressStore extends LocalModel {
    constructor() {
        super('elearningProgress', {
            namePlural: 'elearningProgresses',
            namePluralReal: 'elearningprogress',
            sort: '-completedDate',
            limit: 1000,
            api: {
                search: {
                    url: '/api/elearningprogress/mine',
                    params: {},
                },
                load: {
                    url: '/api/elearningprogress/mine',
                    params: {},
                },
                save: {
                    url: '/api/elearningprogress/',
                    params: {},
                },
                delete: {
                    url: '/api/elearningprogress/',
                    params: {},
                },
            },
        });
    }

    @observable elearningProgress = {};

    /** All of the caller's progress rows (one per course). */
    @observable elearningProgresses = [];

    /** Result of the last markLessonDone call: { passed, wrong, badgeEarned, invitedToExam }. */
    @observable lastResult = {};

    /** Admin results table rows for one course. */
    @observable adminRows = [];

    @action
    async loadMine() {
        const response = await util.fetchApi('/api/elearningprogress/mine', { method: 'GET' }, {});
        if (response?.status === 200) {
            this.updateKeyValue('elearningProgresses', response.data || []);
        }
        return response;
    }

    @action
    replaceRow(row) {
        if (!row || !row.course) {
            return;
        }
        const rest = this.elearningProgresses.filter(p => p.course !== row.course);
        this.updateKeyValue('elearningProgresses', [...rest, row]);
        this.updateKeyValue('elearningProgress', row);
    }

    @action
    async start(courseId) {
        const response = await util.fetchApi(`/api/elearningprogress/${courseId}/start`, { method: 'POST' }, { hostname: hostname() });
        if (response?.status === 200 || response?.status === 201) {
            this.replaceRow(response.data);
        }
        return response;
    }

    /**
     * @returns {{ passed: boolean, wrong: string[], badgeEarned?: boolean, invitedToExam?: boolean }}
     */
    @action
    async markLessonDone(courseId, lessonId, answers = [], secondsSpent = 0) {
        const response = await util.fetchApi(
            `/api/elearningprogress/${courseId}/lesson/${lessonId}/done`,
            { method: 'POST' },
            { answers, secondsSpent, hostname: hostname() },
        );
        const data = response?.data || { passed: false, wrong: [] };
        this.updateKeyValue('lastResult', data);
        if (response?.status === 200 && data.passed && data.progress) {
            this.replaceRow(data.progress);
        }
        return data;
    }

    /**
     * Report active seconds spent on a lesson (sent when the learner leaves it).
     * The server re-runs the completion step, so the reply may carry badgeEarned.
     */
    @action
    async addTimeSpent(courseId, lessonId, seconds) {
        if (!(seconds > 0)) {
            return null;
        }
        const response = await util.fetchApi(
            `/api/elearningprogress/${courseId}/lesson/${lessonId}/time`,
            { method: 'POST' },
            { seconds, hostname: hostname() },
        );
        const data = response?.data || null;
        if (response?.status === 200 && data && data.progress) {
            this.replaceRow(data.progress);
        }
        return data;
    }

    @action
    async undoLesson(courseId, lessonId) {
        const response = await util.fetchApi(`/api/elearningprogress/${courseId}/lesson/${lessonId}/undo`, { method: 'POST' }, { hostname: hostname() });
        if (response?.status === 200) {
            this.replaceRow(response.data);
        }
        return response;
    }

    @action
    async loadAdminRows(courseId) {
        const response = await util.fetchApi('/api/elearningprogress/admin/', { method: 'GET' }, { course: courseId });
        if (response?.status === 200) {
            this.updateKeyValue('adminRows', response.data || []);
        }
        return response;
    }

    progressFor(courseId) {
        return this.elearningProgresses.find(p => p.course === Number(courseId)) || null;
    }
}

const store = new ElearningProgressStore();
export default store;
